import { apiClient } from '@/shared/lib/apiClient';
import type {
  ApiResponse,
  AuthIdentitySummary,
  AuthResponse,
  MeResponse,
} from '@/types';
import type { SmsConsentSource } from '@/shared/config/legal';

const deviceInfo = { platform: 'web' as const, deviceLabel: 'Travel Guider Web' };

export type SendOtpConsentPayload = {
  smsConsent: true;
  smsConsentVersion: string;
  smsConsentSource: SmsConsentSource;
};

export const authApi = {
  async sendOtp(phone: string, consent: SendOtpConsentPayload) {
    const response = await apiClient.post<ApiResponse<{ message: string }>>('/auth/send-otp', {
      phone,
      ...consent,
    });
    return response.data.data!;
  },

  async resendOtp(phone: string, consent: SendOtpConsentPayload) {
    const response = await apiClient.post<ApiResponse<{ message: string }>>('/auth/resend-otp', {
      phone,
      ...consent,
    });
    return response.data.data!;
  },

  async verifyOtp(
    phone: string,
    otp: string,
    options?: { intent?: string; from?: string; travelerSearch?: boolean }
  ): Promise<AuthResponse & { isNewUser?: boolean }> {
    const response = await apiClient.post<ApiResponse<AuthResponse & { isNewUser?: boolean }>>(
      '/auth/verify-otp',
      {
        phone,
        otp,
        deviceInfo,
        intent: options?.intent,
        from: options?.from,
        travelerSearch: options?.travelerSearch,
      }
    );
    return response.data.data!;
  },

  async googleAuth(
    idToken: string,
    options?: { intent?: string; from?: string; travelerSearch?: boolean }
  ): Promise<AuthResponse & { isNewUser?: boolean }> {
    const response = await apiClient.post<ApiResponse<AuthResponse & { isNewUser?: boolean }>>(
      '/auth/google',
      {
        idToken,
        deviceInfo,
        intent: options?.intent,
        from: options?.from,
        travelerSearch: options?.travelerSearch,
      }
    );
    return response.data.data!;
  },

  async getMe(options?: {
    intent?: string;
    from?: string;
    travelerSearch?: boolean;
  }): Promise<MeResponse> {
    const response = await apiClient.get<ApiResponse<MeResponse>>('/auth/me', {
      params: {
        intent: options?.intent,
        from: options?.from,
        travelerSearch: options?.travelerSearch ? true : undefined,
      },
    });
    return response.data.data!;
  },

  // ─── Account & Security ───
  // Linking a phone to the account you are already signed in with. This is
  // the repair path when someone signed in with a Google address we had
  // never seen and so landed on a fresh account.

  async listIdentities(): Promise<AuthIdentitySummary[]> {
    const response =
      await apiClient.get<ApiResponse<AuthIdentitySummary[]>>('/auth/identities');
    return response.data.data!;
  },

  async startPhoneLink(
    phone: string,
    consent: SendOtpConsentPayload
  ): Promise<{ message: string }> {
    const response = await apiClient.post<ApiResponse<{ message: string }>>(
      '/auth/identities/phone/start',
      { phone, ...consent }
    );
    return response.data.data!;
  },

  async confirmPhoneLink(
    phone: string,
    otp: string
  ): Promise<{ identities: AuthIdentitySummary[] }> {
    const response = await apiClient.post<
      ApiResponse<{ identities: AuthIdentitySummary[] }>
    >('/auth/identities/phone/confirm', { phone, otp });
    return response.data.data!;
  },

  async logout() {
    await apiClient.post('/auth/logout');
  },

  async logoutAll() {
    await apiClient.post('/auth/logout-all');
  },
};

/** @deprecated Use authApi from '@/features/auth/api/auth.api' */
export const authService = authApi;
